import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Download, Users, Flame, Star, Send } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { DashboardFilters } from "@/components/admin/DashboardFilters";
import { LeadsTable } from "@/components/admin/LeadsTable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], profiles: {} });
  const [filters, setFilters] = useState({ profile: "", status: "", source: "", category: "", search: "" });
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resending, setResending] = useState(false);

  const resendNotifications = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/admin/resend-notifications", null, { params: { days: 2 } });
      toast.success(`Notifications renvoyées : ${data.leads} lead(s) + ${data.bookings} demande(s).`);
      setResendOpen(false);
    } catch (err) {
      toast.error("Échec du renvoi. Réessayez.");
    } finally {
      setResending(false);
    }
  };

  const load = useCallback(() => {
    const params = { archived: view };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    return api.get("/admin/leads", { params }).then((r) => {
      setLeads(r.data.leads);
      setLoading(false);
    });
  }, [filters, view]);

  useEffect(() => { api.get("/admin/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const sources = [...new Set(leads.map((l) => l.source))].filter(Boolean);

  const askDelete = (e, lead) => {
    e.stopPropagation();
    setPendingDelete(lead);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setDeleting(true);
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead supprimé");
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error("Échec de la suppression. Réessayez.");
    } finally {
      setDeleting(false);
    }
  };

  const archiveLead = async (e, id, archived) => {
    e.stopPropagation();
    await api.patch(`/admin/leads/${id}/archive`, { archived });
    toast.success(archived ? "Lead archivé" : "Lead désarchivé");
    load();
  };

  const stats = {
    total: leads.length,
    hot: leads.filter((l) => l.category === "Lead chaud").length,
    prio: leads.filter((l) => l.category === "Lead prioritaire").length,
  };

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold text-[#1a2d56]">Leads</h1>
          <p className="text-[#4b5b7c] text-sm mt-1">Qualification et suivi de vos prospects Restart.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full bg-[#f0f2f8] p-1" data-testid="view-toggle">
            {[
              { key: "active", label: "Actifs" },
              { key: "archived", label: "Archivés" },
              { key: "all", label: "Tous" },
            ].map((v) => (
              <button
                key={v.key}
                data-testid={`view-${v.key}`}
                onClick={() => setView(v.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  view === v.key ? "bg-[#1a2d56] text-white" : "text-[#4b5b7c] hover:text-[#1a2d56]"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <a
            href={`${BACKEND_URL}/api/admin/leads/export`}
            data-testid="export-csv-button"
            className="inline-flex items-center gap-2 bg-[#1a2d56] text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-[#1a2d56]/90"
          >
            <Download size={16} /> Export CSV
          </a>
          <button
            data-testid="resend-notifications-button"
            onClick={() => setResendOpen(true)}
            className="inline-flex items-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-[#c9d02f]"
          >
            <Send size={16} /> Renvoyer les notifications
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard icon={Users} label="Total leads" value={stats.total} />
        <StatCard icon={Flame} label="Leads chauds" value={stats.hot} accent />
        <StatCard icon={Star} label="Prioritaires" value={stats.prio} accent />
      </div>

      <DashboardFilters filters={filters} setFilters={setFilters} meta={meta} sources={sources} />

      <LeadsTable
        loading={loading}
        leads={leads}
        onRowClick={(id) => navigate(`/admin/leads/${id}`)}
        onArchive={archiveLead}
        onDelete={askDelete}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement ce lead ?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? `${pendingDelete.first_name} ${pendingDelete.last_name || ""} — ${pendingDelete.email}. ` : ""}
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600">
              {deleting ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resendOpen} onOpenChange={(o) => { if (!o) setResendOpen(false); }}>
        <AlertDialogContent data-testid="resend-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Renvoyer les notifications internes ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les e-mails internes (leads/tests + demandes de bilan) des <strong>2 derniers jours</strong> seront
              renvoyés à sante@bhealth.be et elysee@bhealth.be. Les prospects ne sont pas re-contactés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="resend-cancel-button" disabled={resending}>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="resend-confirm-button" disabled={resending}
              onClick={(e) => { e.preventDefault(); resendNotifications(); }}
              className="bg-[#1a2d56] hover:bg-[#111e3a]">
              {resending ? "Envoi…" : "Renvoyer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent ? "bg-[#d7de3b] text-[#1a2d56]" : "bg-[#f0f2f8] text-[#1a2d56]"}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-2xl font-head font-extrabold text-[#1a2d56]">{value}</div>
        <div className="text-xs text-[#4b5b7c]">{label}</div>
      </div>
    </div>
  );
}
